import type { Order } from '@prody/shared';
import { billableQuantity, computeOrderTotals } from '@prody/shared';

/* ------------------------------------------------------------------ *
 * The live feed carries more than the core domain model: the client's
 * trade code, the person who ordered, what was paid, the terminal.
 * It rides in `order.custom`; these readers are the one place that
 * knows the keys, so screens never touch the bag directly.
 * ------------------------------------------------------------------ */

export interface OrderMeta {
  /** Short client code — "ESP", "ALD". The column the floor reads. */
  tradeName: string | null;
  /** The person on the account who placed it. */
  contactName: string | null;
  phone: string | null;
  paymentType: string | null;
  terminal: string | null;
  /** Paid so far, minor units. null = the feed did not say. */
  paidMinor: number | null;
  /** Per-line unit cost in minor units, keyed by line id. */
  lineCosts: Record<string, number> | null;
  /** What the customer asked for in their own words — "3 und", "16 kg". */
  lineRequests: Record<string, string> | null;
}

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.length > 0 ? v : null;

const record = <T>(v: unknown): Record<string, T> | null =>
  v && typeof v === 'object' ? (v as Record<string, T>) : null;

export const orderMeta = (order: Order): OrderMeta => {
  const bag = (order.custom ?? {}) as Record<string, unknown>;
  return {
    tradeName: str(bag['tradeName']),
    contactName: str(bag['contactName']),
    phone: str(bag['phone']),
    paymentType: str(bag['paymentType']),
    terminal: str(bag['terminal']),
    paidMinor: typeof bag['paidMinor'] === 'number' ? bag['paidMinor'] : null,
    lineCosts: record<number>(bag['lineCosts']),
    lineRequests: record<string>(bag['lineRequests']),
  };
};

/** Cost of goods for the order, minor units. null when the feed had no costs. */
export const orderCogsMinor = (order: Order): number | null => {
  const costs = orderMeta(order).lineCosts;
  if (!costs || Object.keys(costs).length === 0) return null;
  return order.lines.reduce(
    (sum, line) => sum + (costs[line.id as string] ?? 0) * billableQuantity(line),
    0,
  );
};

export interface SelectionStats {
  count: number;
  grossMinor: number;
  netMinor: number;
  cogsMinor: number | null;
  profitMinor: number | null;
  paidMinor: number | null;
  balanceMinor: number | null;
}

/** The bulk bar's arithmetic — the numbers the old admin showed for a selection. */
export const selectionStats = (
  orders: readonly Order[],
  taxBpsFor: (key: string) => number,
): SelectionStats => {
  let gross = 0;
  let net = 0;
  let cogs = 0;
  let cogsKnown = false;
  let paid = 0;
  let paidKnown = false;

  for (const order of orders) {
    const totals = computeOrderTotals(order, taxBpsFor);
    gross += totals.gross.minor;
    net += totals.net.minor;
    const c = orderCogsMinor(order);
    if (c !== null) {
      cogs += c;
      cogsKnown = true;
    }
    const meta = orderMeta(order);
    if (meta.paidMinor !== null) {
      paid += meta.paidMinor;
      paidKnown = true;
    }
  }

  return {
    count: orders.length,
    grossMinor: gross,
    netMinor: net,
    cogsMinor: cogsKnown ? cogs : null,
    profitMinor: cogsKnown ? net - cogs : null,
    paidMinor: paidKnown ? paid : null,
    balanceMinor: paidKnown ? gross - paid : null,
  };
};
